import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EditUsernameModal from '../components/EditUsernameModal';
import { settingsAPI } from '../config/api';
import { updateShippingSettingsCache } from '../hooks/useShippingSettings';
import { getDefaultZones, SHIPPING_ZONE_KEYS, ZONE_LABELS } from '../utils/shippingZones';
import './SettingsPage.css';

// Configurazione API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shop/backend/api' 
  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

const zoneSections = [
  {
    key: 'italy',
    description: 'Ordini con indirizzo di spedizione in Italia.',
  },
  {
    key: 'eu',
    description: "Destinazioni all'interno dell'Unione Europea (esclusa l'Italia).",
  },
  {
    key: 'world',
    description: 'Spedizioni verso paesi extra-UE.',
  },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditUsernameModal, setShowEditUsernameModal] = useState(false);

  // Stati per spedizione
  const [shippingSettings, setShippingSettings] = useState({
    zones: getDefaultZones(),
    fixed_shipping_enabled: false,
    updated_at: null,
  });
  const [shippingLoading, setShippingLoading] = useState(true);
  const [shippingSaving, setShippingSaving] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const [shippingSuccess, setShippingSuccess] = useState(null);
  
  // Stati per la modifica password
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  const fetchShippingSettings = async () => {
    try {
      setShippingLoading(true);
      setShippingError(null);
      const data = await settingsAPI.getShipping();
      const normalized = {
        zones: {
          ...getDefaultZones(),
          ...(data?.zones || {}),
        },
        fixed_shipping_enabled: data?.fixed_shipping_enabled ?? false,
        updated_at: data?.updated_at ?? null,
      };
      setShippingSettings(normalized);
      updateShippingSettingsCache(normalized);
    } catch (err) {
      setShippingError(err.message || 'Errore nel recupero delle impostazioni di spedizione.');
    } finally {
      setShippingLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    fetchShippingSettings();
  }, []);

  const checkAuth = () => {
    console.log('🔍 Verifica autenticazione locale...');
    
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    const adminData = localStorage.getItem('admin_data');
    
    if (!isLoggedIn || !adminData) {
      console.log('❌ Non autenticato, reindirizzamento al login...');
      navigate('/amministrazione');
      return;
    }
    
    console.log('✅ Autenticazione verificata!');
    setAdmin(JSON.parse(adminData));
    setLoading(false);
  };

  const handleEditUsername = async (newUsername, currentPassword) => {
    try {
      console.log('👤 Aggiornamento username:', newUsername);
      
      // Chiamata API per aggiornare l'username
      const response = await fetch(`${API_BASE_URL}/admin.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'update_username',
          username: newUsername,
          current_password: currentPassword,
          current_username: admin?.username || 'stibbert-admin'
        }),
      });

      const data = await response.json();
      console.log('📋 Risposta aggiornamento username:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'aggiornamento dell\'username');
      }

      // Aggiorna i dati locali
      const updatedAdmin = { ...admin, username: newUsername };
      setAdmin(updatedAdmin);
      localStorage.setItem('admin_data', JSON.stringify(updatedAdmin));
      
      setShowEditUsernameModal(false);
    } catch (err) {
      console.error('💥 Errore aggiornamento username:', err);
      throw err;
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validazioni
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Le password non coincidono');
      setPasswordLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('La nuova password deve essere di almeno 6 caratteri');
      setPasswordLoading(false);
      return;
    }

    try {
      console.log('🔒 Cambio password in corso...');
      
      // Chiamata API per cambiare la password
      const response = await fetch(`${API_BASE_URL}/admin.php`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'update_password',
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          confirm_password: passwordData.confirmPassword,
          current_username: admin?.username || 'stibbert-admin'
        }),
      });

      const data = await response.json();
      console.log('📋 Risposta cambio password:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante il cambio password');
      }

      setPasswordSuccess('Password aggiornata con successo!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
    } catch (err) {
      console.error('💥 Errore cambio password:', err);
      setPasswordError(err.message || 'Errore durante il cambio password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleZoneInputChange = (zoneKey, field, value) => {
    setShippingSettings((prev) => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneKey]: {
          ...prev.zones?.[zoneKey],
          [field]: value,
        },
      },
    }));
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setShippingSaving(true);
    setShippingSuccess(null);
    setShippingError(null);

    try {
      const payload = {
        zones: SHIPPING_ZONE_KEYS.reduce((acc, key) => {
          const zone = shippingSettings.zones?.[key] || {};
          acc[key] = {
            free_shipping_min: parseFloat(zone.free_shipping_min) || 0,
            shipping_cost: parseFloat(zone.shipping_cost) || 0,
          };
          return acc;
        }, {}),
        fixed_shipping_enabled: shippingSettings.fixed_shipping_enabled ?? false,
      };

      const data = await settingsAPI.updateShipping(payload);
      const normalized = {
        zones: {
          ...getDefaultZones(),
          ...(data?.zones || payload.zones),
        },
        fixed_shipping_enabled: data?.fixed_shipping_enabled ?? payload.fixed_shipping_enabled ?? false,
        updated_at: data?.updated_at ?? new Date().toISOString(),
      };
      setShippingSettings(normalized);
      updateShippingSettingsCache(normalized);
      setShippingSuccess('Impostazioni di spedizione aggiornate con successo!');
    } catch (err) {
      setShippingError(err.message || 'Errore durante il salvataggio delle impostazioni di spedizione.');
    } finally {
      setShippingSaving(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('it-IT');
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="settings-page admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src={`${publicUrl}/logomuseo.png`} alt="Museo Stibbert" className="sidebar-logo" />
    
        </div>

        <div className="sidebar-admin-title">
          <h1><strong style={{color: 'var(--bianco), font-weight: 700, font-size: 1.2rem'}}>Museo Stibbert</strong> Amministrazione</h1>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/amministrazione/dashboard')}>
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/categorie')}>
            <i className="fas fa-tags"></i>
            <span>Categorie</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/prodotti')}>
            <i className="fas fa-box"></i>
            <span>Prodotti</span>
          </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/ordini')}>
            <i className="fas fa-shopping-cart"></i>
            <span>Ordini</span>
          </button>
          <button className="nav-item active">
            <i className="fas fa-cog"></i>
            <span>Impostazioni</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-item" onClick={() => {
            localStorage.removeItem('admin_logged_in');
            localStorage.removeItem('admin_data');
            navigate('/amministrazione');
          }}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="page-header">
          <div className="header-content">
            <h1>Impostazioni</h1>
          </div>
        </div>

        <div className="main-content">
          {/* Container Spedizione */}
          <div className="settings-container">
            <h2>Spedizione e soglia gratuita</h2>
            <p className="settings-description">
              Configura importi minimi e costi di spedizione per Italia, Unione Europea e paesi extra-UE.
              I valori vengono applicati automaticamente nel carrello e aggiornati dinamicamente al checkout in base all'indirizzo inserito dal cliente.
            </p>

            <form onSubmit={handleShippingSubmit} className="settings-form">
              <div className="shipping-zones-grid">
                {zoneSections.map(({ key, description }) => {
                  const zone = shippingSettings.zones?.[key] || {};
                  return (
                    <div className="zone-card" key={key}>
                      <div className="zone-card-header">
                        <h3>{ZONE_LABELS[key]}</h3>
                        <span className="zone-tag">
                          {key === 'italy' ? 'Italia' : key === 'eu' ? 'UE' : 'Extra-UE'}
                        </span>
                      </div>
                      <p className="zone-card-description">{description}</p>
                      <div className="zone-inputs">
                        <div className="settings-item">
                          <label>Ordine minimo per spedizione gratuita (€)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={zone.free_shipping_min ?? 0}
                            onChange={(e) => handleZoneInputChange(key, 'free_shipping_min', e.target.value)}
                            disabled={shippingLoading || shippingSaving}
                          />
                        </div>
                        <div className="settings-item">
                          <label>Costo spedizione (€)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={zone.shipping_cost ?? 0}
                            onChange={(e) => handleZoneInputChange(key, 'shipping_cost', e.target.value)}
                            disabled={shippingLoading || shippingSaving}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="fixed-shipping-option">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id="fixed-shipping-checkbox"
                    checked={shippingSettings.fixed_shipping_enabled ?? false}
                    onChange={(e) => {
                      setShippingSettings((prev) => ({
                        ...prev,
                        fixed_shipping_enabled: e.target.checked,
                      }));
                    }}
                    disabled={shippingLoading || shippingSaving}
                    className="fixed-shipping-checkbox"
                  />
                  <label htmlFor="fixed-shipping-checkbox" className="checkbox-label">
                    <span className="checkbox-custom"></span>
                    <span className="checkbox-text">Applica sempre il costo fisso di spedizione</span>
                  </label>
                </div>
                <p className="settings-hint">
                  Se abilitato, il costo di spedizione verrà sempre applicato indipendentemente dal valore del carrello. 
                  Il banner "aggiungi ancora X€ per avere le spedizioni gratis" non verrà mostrato.
                </p>
              </div>

              {shippingError && (
                <div className="error-message">
                  ⚠️ {shippingError}
                </div>
              )}

              {shippingSuccess && (
                <div className="success-message">
                  ✅ {shippingSuccess}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={shippingLoading || shippingSaving}
              >
                {shippingSaving ? 'Salvataggio...' : 'Salva impostazioni'}
              </button>

              {shippingSettings.updated_at && (
                <p className="settings-hint">
                  Ultimo aggiornamento: {formatDateTime(shippingSettings.updated_at)}
                </p>
              )}
            </form>
          </div>

          {/* Container Username */}
          <div className="settings-container">
            <h2>Informazioni Account</h2>
            <div className="settings-item">
              <label>Nome Utente</label>
              <div className="input-with-button">
                <input 
                  type="text" 
                  value={admin?.username || ''} 
                  readOnly 
                  className="readonly-input"
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowEditUsernameModal(true)}
                >
                  <i className="fas fa-edit"></i>
                  Modifica
                </button>
              </div>
            </div>
          </div>

          {/* Container Password */}
          <div className="settings-container">
            <h2>Cambio Password</h2>
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="settings-item">
                <label htmlFor="currentPassword">Password Attuale</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Inserisci password attuale"
                />
              </div>

              <div className="settings-item">
                <label htmlFor="newPassword">Nuova Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Inserisci nuova password"
                />
              </div>

              <div className="settings-item">
                <label htmlFor="confirmPassword">Ripeti Nuova Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Ripeti nuova password"
                />
              </div>

              {passwordError && (
                <div className="error-message">
                  ⚠️ {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="success-message">
                  ✅ {passwordSuccess}
                </div>
              )}

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={passwordLoading}
              >
                {passwordLoading ? 'Aggiornamento in corso...' : 'Modifica Password'}
              </button>
            </form>
          </div>
        </div>
      </main>

      {/* Modal Modifica Username */}
      <EditUsernameModal
        isOpen={showEditUsernameModal}
        onClose={() => setShowEditUsernameModal(false)}
        onEditUsername={handleEditUsername}
        currentUsername={admin?.username || ''}
      />
    </div>
  );
};

export default SettingsPage;
